/**
 * SIM card helpers shared by the SIM Cards page, the device SIM panel and the
 * settings card. Pure functions only (unit-tested).
 */

export type SimProvider = "kpn" | "webbing" | "telenor" | "tele2";

export type SimCard = {
  id: string;
  iccid: string;
  provider: SimProvider;
  source: "simetric" | "manual";
  msisdn: string | null;
  imsi: string | null;
  imei: string | null;
  status: string | null;
  statusLabel: string | null;
  ratePlan: string | null;
  equipmentInstanceID: string | null;
  slot: number | null;
  label: string | null;
  /** Internal note, stored only in the hub (never sent to the provider). */
  note: string | null;
  simetricSegment1: string | null;
  simetricSegment2: string | null;
  simetricSegment3: string | null;
  /** Billing cycle start (epoch ms, UTC midnight) the CTD values belong to. */
  usageCycle: number | null;
  ctdDataMb: number | null;
  usageUpdatedAt: number | null;
  lastSyncedAt: number | null;
  missingSince: number | null;
  deviceName: string | null;
  equipmentID: string | null;
  /** Profile assigned by hand (used when the rate plan selects none). */
  manualProfileID: string | null;
  /** Effective profile, see `resolveSimProfile`. */
  profileID: string | null;
  profileName: string | null;
  profileVia: SimProfileSource | null;
};

export type SimProfileNetwork = {
  countryCode: string;
  /** Preferred networks, most preferred first. */
  networks: string[];
  note: string | null;
};

export type SimProfile = {
  id: string;
  provider: SimProvider;
  name: string;
  isOpen: boolean;
  /** Carrier rate plan that selects this profile, if any. */
  ratePlan: string | null;
  notes: string | null;
  networks: SimProfileNetwork[];
};

/** How a SIM got its profile: from its carrier rate plan or set by hand. */
export type SimProfileSource = "rate_plan" | "manual";

/**
 * The SIM's profile. A profile tied to the SIM's rate plan wins (the carrier
 * applies the profile through the plan); otherwise the hand-picked one.
 */
export function resolveSimProfile(
  sim: {
    provider: SimProvider;
    ratePlan: string | null;
    manualProfileID: string | null;
  },
  profiles: SimProfile[],
): { profile: SimProfile; via: SimProfileSource } | null {
  if (sim.ratePlan) {
    const byPlan = profiles.find(
      (p) => p.provider === sim.provider && p.ratePlan === sim.ratePlan,
    );
    if (byPlan) return { profile: byPlan, via: "rate_plan" };
  }
  const manual = sim.manualProfileID
    ? profiles.find((p) => p.id === sim.manualProfileID)
    : undefined;
  return manual ? { profile: manual, via: "manual" } : null;
}

/** Whether the rate plan already fixes the profile (no manual choice). */
export function hasRatePlanProfile(
  sim: { provider: SimProvider; ratePlan: string | null },
  profiles: SimProfile[],
) {
  return (
    !!sim.ratePlan &&
    profiles.some(
      (p) => p.provider === sim.provider && p.ratePlan === sim.ratePlan,
    )
  );
}

const regionNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

export function countryName(code: string) {
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Network(s) a profile uses in a country, most preferred first. Open profiles
 * without a preference there use any network; null means nothing is known.
 */
export function profileNetworkIn(
  profile: SimProfile | null | undefined,
  countryCode: string,
): string | null {
  if (!profile) return null;
  const entry = profile.networks.find((n) => n.countryCode === countryCode);
  if (entry && entry.networks.length > 0) return entry.networks.join(" › ");
  return profile.isOpen ? "Any network" : null;
}

/** Countries covered by any of the profiles, in first-seen order. */
export function profileCountries(profiles: SimProfile[]) {
  const seen = new Set<string>();
  for (const profile of profiles) {
    for (const network of profile.networks) seen.add(network.countryCode);
  }
  return Array.from(seen);
}

/**
 * Fixed `link_id` of the global document link (type `sim_cards`) that points
 * to the shared SIM Cards docs. Seeded by migration 20261001130000_sim_docs.
 */
export const SIM_DOCS_LINK_ID = "51ca2d00-0000-4000-8000-000000000001";

export const SIM_DEVICE_TAGS_SETTING = "sim_device_tags";
export type SimDeviceTagsSetting = { tagIds: string[] };

export const PROVIDER_LABELS: Record<SimProvider, string> = {
  kpn: "KPN",
  webbing: "Webbing",
  telenor: "Telenor",
  tele2: "Tele2",
};

export type SimProviderInfo = {
  /** APN to configure in the modem. */
  apn: string;
  /** Data price in Rappen (CHF 0.01) per GB. */
  priceRappenPerGb: number;
};

/** Static per-provider facts shown in the SIM details. */
export const PROVIDER_INFO: Record<SimProvider, SimProviderInfo> = {
  tele2: { apn: "m2m.tele2.com", priceRappenPerGb: 30 },
  telenor: { apn: "eliot.internet", priceRappenPerGb: 18 },
  webbing: { apn: "wbdata", priceRappenPerGb: 18 },
  kpn: { apn: "tnf.m2m", priceRappenPerGb: 20 },
};

/** Price per GB as `CHF 0.30 / GB`. */
export function formatPricePerGb(rappen: number) {
  return `CHF ${(rappen / 100).toFixed(2)} / GB`;
}

/** UI copy of the server capability matrix (the server enforces it). */
export const PROVIDER_CAN_TOGGLE: Record<SimProvider, boolean> = {
  kpn: false,
  webbing: true,
  telenor: true,
  tele2: false,
};

export type SimStatusKind =
  | "active"
  | "idle"
  | "suspended"
  | "deactivated"
  | "inventory"
  | "missing"
  | "unknown";

export function simStatusKind(
  sim: Pick<SimCard, "status" | "missingSince">,
): SimStatusKind {
  if (sim.missingSince) return "missing";
  switch ((sim.status ?? "").toUpperCase()) {
    case "ACTIVATED":
      return "active";
    case "ACTIVATION_READY":
      return "idle";
    case "SUSPEND":
    case "SUSPENDED":
      return "suspended";
    case "DEACTIVATED":
      return "deactivated";
    case "INVENTORY":
      return "inventory";
    default:
      return "unknown";
  }
}

export const STATUS_LABELS: Record<SimStatusKind, string> = {
  active: "Active",
  idle: "Idle",
  suspended: "Suspended",
  deactivated: "Deactivated",
  inventory: "Inventory",
  missing: "Missing",
  unknown: "Unknown",
};

/** Whether the CTD values belong to the billing cycle running at `now`. */
export function isCurrentCycle(
  cycleStart: number | null | undefined,
  now: Date = new Date(),
) {
  if (cycleStart === null || cycleStart === undefined) return false;
  const start = new Date(cycleStart);
  const end = new Date(
    Date.UTC(
      start.getUTCFullYear(),
      start.getUTCMonth() + 1,
      start.getUTCDate(),
    ),
  );
  return now.getTime() >= start.getTime() && now.getTime() < end.getTime();
}

/** Data used in the current cycle (0 when the CTD belongs to an old cycle). */
export function currentMonthUsageMb(
  sim: Pick<SimCard, "usageCycle" | "ctdDataMb">,
  now: Date = new Date(),
) {
  return isCurrentCycle(sim.usageCycle, now) ? (sim.ctdDataMb ?? 0) : 0;
}

export function formatDataMb(mb: number | null | undefined) {
  const value = mb ?? 0;
  if (value === 0) return "0 MB";
  if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(2)} TB`;
  if (value >= 1024)
    return `${(value / 1024).toFixed(value >= 10240 ? 1 : 2)} GB`;
  if (value < 1) return `${(value * 1024).toFixed(0)} KB`;
  return `${value.toFixed(value >= 100 ? 0 : 1)} MB`;
}

export function shortIccid(iccid: string) {
  return iccid.length > 10 ? `…${iccid.slice(-8)}` : iccid;
}

/** First day of the month (UTC) as `YYYY-MM-DD`. */
export function monthKey(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

export function monthKeyFromEpoch(ms: number) {
  return monthKey(new Date(ms));
}

/** The last `count` month keys ending with the month of `now`. */
export function lastMonthKeys(count: number, now: Date = new Date()) {
  const keys: string[] = [];
  for (let i = count - 1; i >= 0; i--) {
    keys.push(
      monthKey(
        new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)),
      ),
    );
  }
  return keys;
}

export function dayKey(ms: number) {
  return new Date(ms).toISOString().slice(0, 10);
}

export function lastDayKeys(count: number, now: Date = new Date()) {
  const keys: string[] = [];
  const base = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  for (let i = count - 1; i >= 0; i--) {
    keys.push(new Date(base - i * 86_400_000).toISOString().slice(0, 10));
  }
  return keys;
}

export function matchesSimSearch(sim: SimCard, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    sim.iccid,
    sim.msisdn,
    sim.imsi,
    sim.imei,
    sim.deviceName,
    sim.label,
    sim.note,
    sim.simetricSegment1,
    sim.profileName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((part) => haystack.includes(part));
}

export interface SimFilters {
  search: string;
  provider: SimProvider | "all";
  status: SimStatusKind | "all";
  assignment: "all" | "assigned" | "unassigned";
  deviceID: string | "all";
  /** Profile id, "none" for SIMs without a profile, or "all". */
  profile: string;
}

export const DEFAULT_SIM_FILTERS: SimFilters = {
  search: "",
  provider: "all",
  status: "all",
  assignment: "all",
  deviceID: "all",
  profile: "all",
};

export function filterSims(sims: SimCard[], filters: SimFilters) {
  return sims.filter((sim) => {
    if (filters.provider !== "all" && sim.provider !== filters.provider)
      return false;
    if (filters.status !== "all" && simStatusKind(sim) !== filters.status)
      return false;
    if (filters.assignment === "assigned" && !sim.equipmentInstanceID)
      return false;
    if (filters.assignment === "unassigned" && sim.equipmentInstanceID)
      return false;
    if (
      filters.deviceID !== "all" &&
      sim.equipmentInstanceID !== filters.deviceID
    )
      return false;
    if (filters.profile === "none" && sim.profileID) return false;
    if (
      filters.profile !== "all" &&
      filters.profile !== "none" &&
      sim.profileID !== filters.profile
    )
      return false;
    return matchesSimSearch(sim, filters.search);
  });
}

/** Expands tag ids to include all descendants (tags form a tree). */
export function expandTagIds(
  tags: { id: string; parent_id: string | null }[],
  selected: string[],
) {
  const children = new Map<string, string[]>();
  for (const tag of tags) {
    if (!tag.parent_id) continue;
    const list = children.get(tag.parent_id) ?? [];
    list.push(tag.id);
    children.set(tag.parent_id, list);
  }
  const result = new Set<string>();
  const visit = (id: string) => {
    if (result.has(id)) return;
    result.add(id);
    for (const child of children.get(id) ?? []) visit(child);
  };
  for (const id of selected) visit(id);
  return Array.from(result);
}

export interface DeviceUsageRow {
  key: string;
  deviceID: string | null;
  deviceName: string;
  simCount: number;
  thisMonthMb: number;
  lastMonthMb: number;
  last12MonthsMb: number;
  sims: {
    sim: SimCard;
    thisMonthMb: number;
    lastMonthMb: number;
    last12MonthsMb: number;
  }[];
}

export interface MonthlyUsageRow {
  simCardID: string;
  cycleMonth: number;
  dataMb: number;
}

/**
 * Usage ranking by device: this month (current-cycle CTD), last month and the
 * 12-month total (monthly history). Unassigned SIMs are grouped together.
 */
export function computeDeviceUsage(
  sims: SimCard[],
  monthly: MonthlyUsageRow[],
  now: Date = new Date(),
): DeviceUsageRow[] {
  const months = lastMonthKeys(12, now);
  const currentMonth = months[months.length - 1];
  const previousMonth = months[months.length - 2];
  const monthSet = new Set(months);
  const bySim = new Map<string, Map<string, number>>();
  for (const row of monthly) {
    const key = monthKeyFromEpoch(row.cycleMonth);
    if (!monthSet.has(key)) continue;
    const map = bySim.get(row.simCardID) ?? new Map<string, number>();
    map.set(key, (map.get(key) ?? 0) + (row.dataMb ?? 0));
    bySim.set(row.simCardID, map);
  }
  const groups = new Map<string, DeviceUsageRow>();
  for (const sim of sims) {
    const key = sim.equipmentInstanceID ?? "__unassigned__";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        deviceID: sim.equipmentInstanceID,
        deviceName: sim.equipmentInstanceID
          ? (sim.deviceName ?? "Unknown device")
          : "Unassigned",
        simCount: 0,
        thisMonthMb: 0,
        lastMonthMb: 0,
        last12MonthsMb: 0,
        sims: [],
      };
      groups.set(key, group);
    }
    const history = bySim.get(sim.id);
    const thisMonth = Math.max(
      currentMonthUsageMb(sim, now),
      history?.get(currentMonth) ?? 0,
    );
    const lastMonth = history?.get(previousMonth) ?? 0;
    let total = 0;
    for (const month of months) {
      if (month === currentMonth) continue;
      total += history?.get(month) ?? 0;
    }
    total += thisMonth;
    group.simCount++;
    group.thisMonthMb += thisMonth;
    group.lastMonthMb += lastMonth;
    group.last12MonthsMb += total;
    group.sims.push({
      sim,
      thisMonthMb: thisMonth,
      lastMonthMb: lastMonth,
      last12MonthsMb: total,
    });
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.deviceID === null) return 1;
    if (b.deviceID === null) return -1;
    return (
      b.thisMonthMb - a.thisMonthMb ||
      b.last12MonthsMb - a.last12MonthsMb ||
      a.deviceName.localeCompare(b.deviceName)
    );
  });
}

/** Max series drawn in stacked charts; the rest fold into "Other". */
export const MAX_CHART_SERIES = 6;

export function seriesColor(index: number) {
  return index < MAX_CHART_SERIES
    ? `var(--sim-series-${index + 1})`
    : "var(--sim-series-other)";
}

export function simSeriesName(sim: Pick<SimCard, "slot" | "iccid">) {
  return sim.slot ? `Slot ${sim.slot}` : shortIccid(sim.iccid);
}

export type PreviewEntry = {
  simId: string;
  iccid: string;
  outcome: "queued" | "skipped";
  operation: string | null;
  reason?: string;
};

export type ActionResponse = {
  batchId: string | null;
  preview: PreviewEntry[];
};

/** Minimum digits for matching a SIM by the end of its ICCID. */
export const ICCID_SUFFIX_MIN_DIGITS = 4;

/**
 * SIMs matching a typed or scanned ICCID: an exact match wins, otherwise every
 * SIM whose ICCID ends with the entered digits (at least 4). Non-digits such as
 * spaces or a trailing "F" filler are ignored.
 */
export function findSimsByIccid<T extends Pick<SimCard, "iccid">>(
  sims: T[],
  input: string,
): T[] {
  const digits = input.replace(/\D/g, "");
  if (!digits) return [];
  const exact = sims.filter((s) => s.iccid.replace(/\D/g, "") === digits);
  if (exact.length > 0) return exact;
  if (digits.length < ICCID_SUFFIX_MIN_DIGITS) return [];
  return sims.filter((s) => s.iccid.replace(/\D/g, "").endsWith(digits));
}

/** Luhn check digit for the first 14 IMEI digits. */
export function imeiCheckDigit(first14: string) {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    let d = Number(first14[i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return String((10 - (sum % 10)) % 10);
}

/**
 * The 15-digit IMEI of a network-reported value. Some carriers report the
 * 16-digit IMEISV (14 IMEI digits + 2 software version digits); the check
 * digit is then recomputed. Returns null for anything that is not an IMEI.
 */
export function normalizeImei(raw: string | null | undefined) {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length === 15) return digits;
  if (digits.length === 14 || digits.length === 16) {
    const first14 = digits.slice(0, 14);
    return first14 + imeiCheckDigit(first14);
  }
  return null;
}

/** Type Allocation Code (make/model prefix) of an IMEI. */
export function imeiTac(raw: string | null | undefined) {
  return normalizeImei(raw)?.slice(0, 8) ?? null;
}
